/**
 * Campus destination landmarks.
 *
 * These are deliberately destination-scale signals, not loose decoration:
 * seven catalog buildings provide the destination silhouettes. Auxiliary
 * catalog roof details and procedural skyline pieces sit inside plot
 * colliders and add no navigation or service ownership.
 */
import * as pc from 'playcanvas'
import { getCachedAsset, getPropEntry, onAssetReady } from '../assets'
import type { AssetZoneController } from '../assetZones'
import { box, matte } from './districtShared'
import {
  PLOTS,
  buildingPlacement,
  plotDoorPosition,
  plotFrontZ,
} from './district'

export interface CampusLandmarkDef {
  plotId: string
  destination: string
  role:
    | 'civic-hall'
    | 'control-station'
    | 'exchange-lantern'
    | 'maker-hall'
    | 'corner-cafe'
    | 'reading-house'
    | 'secure-monolith'
  file: string
  placement: 'building'
}

export const CAMPUS_LANDMARKS: readonly CampusLandmarkDef[] = [
  { plotId: 'P1', destination: 'BOARD', role: 'civic-hall', file: 'synty_city_city_hall_01.glb', placement: 'building' },
  { plotId: 'P2', destination: 'OPS', role: 'control-station', file: 'synty_city_station_03.glb', placement: 'building' },
  { plotId: 'P4', destination: 'EXCHANGE', role: 'exchange-lantern', file: 'synty_city_office_round_01.glb', placement: 'building' },
  { plotId: 'P5', destination: 'WORKSHOP', role: 'maker-hall', file: 'synty_apocalypse_auto_repair_01.glb', placement: 'building' },
  { plotId: 'P6', destination: 'CAFE', role: 'corner-cafe', file: 'synty_city_shop_corner_01.glb', placement: 'building' },
  { plotId: 'P7', destination: 'LIBRARY', role: 'reading-house', file: 'synty_town_shop_03.glb', placement: 'building' },
  { plotId: 'P8', destination: 'DATACENTRE', role: 'secure-monolith', file: 'synty_apocalypse_commercial_large_01.glb', placement: 'building' },
] as const

export function campusLandmarkEntityName(landmark: CampusLandmarkDef): string {
  return `plot-${landmark.plotId}-building`
}

export const CAMPUS_ASSET_PROXIMITY_M = 20

export interface CampusAuxiliaryAssetDef {
  file: string
  entityName: string
  plotId: string
  role: 'maker-hall' | 'cooling-crown'
}

/** Full rear maker hall plus a separately roof-mounted Datacentre plant. */
export const CAMPUS_AUXILIARY_ASSETS: readonly CampusAuxiliaryAssetDef[] = [
  {
    file: 'synty_apocalypse_industrial_large_01.glb',
    entityName: 'campus-P5-maker-hall',
    plotId: 'P5',
    role: 'maker-hall',
  },
  {
    file: 'synty_apocalypse_cooling_tower_01.glb',
    entityName: 'campus-P8-cooling-crown',
    plotId: 'P8',
    role: 'cooling-crown',
  },
] as const

/** Exact files owned by the campus zone. Used by runtime and evidence gates. */
export const CAMPUS_ZONE_ASSET_FILES: readonly string[] = [
  ...CAMPUS_LANDMARKS.map((landmark) => landmark.file),
  ...CAMPUS_AUXILIARY_ASSETS.map((asset) => asset.file),
].sort()

/** Load the campus catalog only after the player reaches a landmark frontage. */
export function isNearCampusAssetZone(
  x: number,
  z: number,
  proximity = CAMPUS_ASSET_PROXIMITY_M,
): boolean {
  return CAMPUS_LANDMARKS.some((landmark) => {
    const plot = PLOTS.find((item) => item.id === landmark.plotId)
    if (!plot) return false
    const [doorX, doorZ] = plotDoorPosition(plot)
    return Math.hypot(x - doorX, z - doorZ) <= proximity
  })
}

function attachWorkshopMakerHall(
  parent: pc.Entity,
  assetZones?: AssetZoneController<pc.Asset>,
): void {
  const detail = CAMPUS_AUXILIARY_ASSETS.find((asset) => asset.role === 'maker-hall')
  const plot = PLOTS.find((item) => item.id === detail?.plotId)
  if (!detail || !plot) return

  const scale = 0.85
  const yaw = 180
  const targetX = (plot.minX + plot.maxX) / 2
  const targetZ = -36.5
  const localCenterX = 0.791591
  const localCenterZ = 2.101641
  const radians = yaw * Math.PI / 180
  const rotatedCenterX = (
    localCenterX * Math.cos(radians) + localCenterZ * Math.sin(radians)
  ) * scale
  const rotatedCenterZ = (
    -localCenterX * Math.sin(radians) + localCenterZ * Math.cos(radians)
  ) * scale
  const holder = new pc.Entity(detail.entityName)
  holder.setLocalScale(scale, scale, scale)
  holder.setPosition(targetX - rotatedCenterX, 0, targetZ - rotatedCenterZ)
  holder.setEulerAngles(0, yaw, 0)
  parent.addChild(holder)

  const attach = (asset: pc.Asset): () => void => {
    if (!asset.resource) throw new Error(`Asset ${detail.file} has no container resource`)
    const container = asset.resource as pc.ContainerResource
    const entity = container.instantiateRenderEntity({ castShadows: true })
    entity.name = 'campus-P5-maker-hall-shell'
    holder.addChild(entity)
    return () => entity.destroy()
  }
  const runtime = getPropEntry(detail.file)?.runtime
  if (assetZones && runtime?.loadClass === 'zone') {
    assetZones.register(runtime.zone, detail.file, attach)
  } else {
    const cached = getCachedAsset(detail.file)
    if (cached) attach(cached)
    else onAssetReady(detail.file, attach)
  }
}

function attachDatacentreCoolingCrown(
  parent: pc.Entity,
  assetZones?: AssetZoneController<pc.Asset>,
): void {
  const detail = CAMPUS_AUXILIARY_ASSETS.find((asset) => asset.role === 'cooling-crown')
  const plot = PLOTS.find((item) => item.id === detail?.plotId)
  const building = plot ? buildingPlacement(plot) : null
  if (!detail || !plot || !building) return
  const holder = new pc.Entity(detail.entityName)
  parent.addChild(holder)

  const attach = (asset: pc.Asset): () => void => {
    if (!asset.resource) throw new Error(`Asset ${detail.file} has no container resource`)
    const container = asset.resource as pc.ContainerResource
    const roofY = 28.354311 * building.scale[1]
    const units = [
      { name: 'campus-P8-cooling-unit-west', x: 27.0 },
      { name: 'campus-P8-cooling-unit-east', x: 32.0 },
    ].map(({ name, x }) => {
      // Keep the imported GLB root transform intact. Overwriting its scale
      // flattened the 48.7 m cooling tower into a 20 cm roof plate.
      const placement = new pc.Entity(name)
      placement.setLocalScale(0.10, 0.10, 0.10)
      placement.setLocalPosition(x, roofY + 2.65, 10.5)
      holder.addChild(placement)
      const entity = container.instantiateRenderEntity({ castShadows: true })
      entity.name = `${name}-shell`
      placement.addChild(entity)
      return placement
    })
    return () => units.forEach((entity) => entity.destroy())
  }
  const runtime = getPropEntry(detail.file)?.runtime
  if (assetZones && runtime?.loadClass === 'zone') {
    assetZones.register(runtime.zone, detail.file, attach)
  } else {
    const cached = getCachedAsset(detail.file)
    if (cached) attach(cached)
    else onAssetReady(detail.file, attach)
  }
}

/**
 * Skyline signals remain inside the already-solid destination plots. They add
 * no colliders and cannot narrow a door or player approach.
 */
function buildDestinationSilhouettes(parent: pc.Entity): void {
  const ops = PLOTS.find((plot) => plot.id === 'P2')!
  const opsX = (ops.minX + ops.maxX) / 2
  const opsFront = plotFrontZ(ops)
  const controlMat = matte(new pc.Color(0.13, 0.19, 0.28), 0.68)
  const signalMat = matte(new pc.Color(0.28, 0.72, 0.94), 0.45)
  box(parent, 'campus-ops-control-tower', [opsX, 11.5, opsFront - 12], [4.4, 23, 4.4], controlMat, true)
  box(parent, 'campus-ops-signal-crown', [opsX, 23.5, opsFront - 12], [6.2, 1, 6.2], signalMat, true)

  const makerMat = matte(new pc.Color(0.96, 0.40, 0.12), 0.42)
  const makerBayMat = matte(new pc.Color(0.035, 0.055, 0.070), 0.72)
  const makerSteelMat = matte(new pc.Color(0.52, 0.66, 0.69), 0.55)
  box(parent, 'campus-P5-maker-bay', [
    31.5,
    3.35,
    -18.10,
  ], [7.2, 6.4, 0.20], makerBayMat, true)
  box(parent, 'campus-P5-maker-crown', [
    31.5,
    6.65,
    -18.0,
  ], [7.8, 0.38, 0.38], makerMat, true)
  for (const x of [27.7, 35.3]) {
    box(parent, `campus-P5-maker-fin-${x}`, [
      x,
      3.4,
      -18.0,
    ], [0.38, 6.8, 0.38], makerMat, true)
  }
  for (const y of [1.2, 2.2, 3.2, 4.2, 5.2]) {
    box(parent, `campus-P5-maker-door-slat-${y}`, [
      31.5,
      y,
      -17.98,
    ], [6.8, 0.08, 0.12], makerSteelMat, true)
  }
  box(parent, 'campus-P5-maker-link-canopy', [
    31.5,
    6.75,
    -16.9,
  ], [7.8, 0.30, 2.3], makerMat, true)
  for (const y of [8.2, 10.2, 12.2]) {
    box(parent, `campus-P5-maker-vent-${y}`, [
      31.5,
      y,
      -18.06,
    ], [8.4, 0.84, 0.12], makerBayMat, true)
  }
  for (const x of [27.0, 36.0]) {
    box(parent, `campus-P5-upper-frame-${x}`, [
      x,
      10.2,
      -18.0,
    ], [0.20, 5.0, 0.20], makerMat, true)
  }

  const datacentre = PLOTS.find((plot) => plot.id === 'P8')!
  const datacentreBuilding = buildingPlacement(datacentre)!
  const datacentreRoofY = 28.354311 * datacentreBuilding.scale[1]
  const secureMat = matte(new pc.Color(0.08, 0.16, 0.24), 0.72)
  const dataSignalMat = matte(new pc.Color(0.18, 0.76, 0.86), 0.52)
  box(parent, 'campus-P8-cooling-plinth', [
    29.5,
    datacentreRoofY + 1.25,
    10.5,
  ], [9.2, 2.5, 3.2], secureMat, true)
  box(parent, 'campus-P8-plant-cap', [
    29.5,
    datacentreRoofY + 2.54,
    10.5,
  ], [9.4, 0.16, 3.4], dataSignalMat, true)
  for (const y of [datacentreRoofY + 0.55, datacentreRoofY + 1.15, datacentreRoofY + 1.75]) {
    box(parent, `campus-P8-plant-louver-${y}`, [
      29.5,
      y,
      8.88,
    ], [8.3, 0.12, 0.10], dataSignalMat, true)
  }
}

export function buildCampusLandmarks(
  parent: pc.Entity,
  assetZones?: AssetZoneController<pc.Asset>,
): void {
  attachWorkshopMakerHall(parent, assetZones)
  attachDatacentreCoolingCrown(parent, assetZones)
  buildDestinationSilhouettes(parent)
}
